import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Flex, Paper, Text, Transition, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import type {ChatInputTip} from './ChatInputTipsData';
import CHAT_INPUT_TIPS from './ChatInputTipsData';

const SHOWN_TIPS_STORAGE_KEY = 'benchling-chat-input-shown-tips';
const TIP_CYCLE_INTERVAL_MS = 120000;
const SLIDE_DURATION_MS = 150;
const SLIDE_OFFSET_PX = 20;

function getShownCounts(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SHOWN_TIPS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as Record<string, number>;
  } catch (err) {
    if (err instanceof SyntaxError) {
      return {};
    }
    throw err;
  }
}

function recordShown(tipId: string): void {
  try {
    const counts = getShownCounts();
    counts[tipId] = (counts[tipId] ?? 0) + 1;
    localStorage.setItem(SHOWN_TIPS_STORAGE_KEY, JSON.stringify(counts));
  } catch (err) {
    if (err instanceof DOMException) {
      return;
    }
    throw err;
  }
}

/** Pick the tip that has been shown the fewest times. Ties are broken randomly. */
function pickLeastShownTip(tips: ReadonlyArray<{id: string}>, counts: Record<string, number>): number {
  let minCount = Infinity;
  for (const tip of tips) {
    const c = counts[tip.id] ?? 0;
    if (c < minCount) {
      minCount = c;
    }
  }
  const candidates: Array<number> = [];
  for (let i = 0; i < tips.length; i++) {
    if ((counts[tips[i].id] ?? 0) === minCount) {
      candidates.push(i);
    }
  }
  return candidates[Math.floor(Math.random() * candidates.length)];
}

/**
 * Return true if the user/tenant has every feature the tip references enabled.
 *
 * Tips that reference features the user doesn't have (e.g. Compose, Ask, Deep Research)
 * are filtered out so we don't advertise features the user cannot use.
 */
export function isTipAvailable(tip: ChatInputTip): boolean {
  if (tip.requiredAgentTypes) {
    for (const agentType of tip.requiredAgentTypes) {
      if (!isAgentTypeEnabled(agentType)) {
        return false;
      }
    }
  }
  if (tip.requiresImageGeneration && !BenchlingConfig.LLM_ENABLE_GOOGLE_CLOUD) {
    return false;
  }
  return true;
}

const useStyles = createStyles((theme) => ({
  icon: {
    color: theme.colors.blue[6],
    fontSize: theme.fontSizes.md,
    flexShrink: 0,
    marginTop: 2,
  },
  title: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 700,
    lineHeight: 1.5,
    color: theme.colors.dark[7],
  },
  text: {
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.5,
    color: theme.colors.dark[7],
  },
  navButton: {
    color: theme.colors.blue[6],
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
  },
}));

type SlideDirection = 'next' | 'previous';
type SlidePhase = 'idle' | 'out' | 'entering' | 'in';

function ChatInputTipsInner({tips}: {tips: ReadonlyArray<ChatInputTip>}): JSX.Element {
  const {classes} = useStyles();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const rafRef = useRef<number>();
  const [currentIndex, setCurrentIndex] = useState(() => {
    const counts = getShownCounts();
    const index = pickLeastShownTip(tips, counts);
    recordShown(tips[index].id);
    return index;
  });
  const [mounted, setMounted] = useState(true);
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('next');
  const [slidePhase, setSlidePhase] = useState<SlidePhase>('idle');

  const navigateToTip = useCallback(
    (nextIndex: number, direction: SlideDirection) => {
      setSlideDirection(direction);
      setSlidePhase('out');
      timeoutRef.current = setTimeout(() => {
        recordShown(tips[nextIndex].id);
        setCurrentIndex(nextIndex);
        setSlidePhase('entering');
      }, SLIDE_DURATION_MS);
    },
    [tips]
  );

  useEffect(() => {
    if (slidePhase !== 'entering') {
      return;
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        setSlidePhase('in');
        timeoutRef.current = setTimeout(() => {
          setSlidePhase('idle');
        }, SLIDE_DURATION_MS);
      });
    });
  }, [slidePhase]);

  const advanceTip = useCallback(() => {
    setMounted(false);
    timeoutRef.current = setTimeout(() => {
      const counts = getShownCounts();
      const nextIndex = pickLeastShownTip(tips, counts);
      recordShown(tips[nextIndex].id);
      setCurrentIndex(nextIndex);
      setMounted(true);
    }, 200);
  }, [tips]);

  const handlePrevious = useCallback(() => {
    if (slidePhase !== 'idle') {
      return;
    }
    const prevIndex = (currentIndex - 1 + tips.length) % tips.length;
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_CHAT_INPUT_TIP_NAVIGATION, {
      direction: 'previous',
      fromTipId: tips[currentIndex].id,
      toTipId: tips[prevIndex].id,
    });
    navigateToTip(prevIndex, 'previous');
  }, [currentIndex, slidePhase, navigateToTip, tips]);

  const handleNext = useCallback(() => {
    if (slidePhase !== 'idle') {
      return;
    }
    const nextIndex = (currentIndex + 1) % tips.length;
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_CHAT_INPUT_TIP_NAVIGATION, {
      direction: 'next',
      fromTipId: tips[currentIndex].id,
      toTipId: tips[nextIndex].id,
    });
    navigateToTip(nextIndex, 'next');
  }, [currentIndex, slidePhase, navigateToTip, tips]);

  useEffect(() => {
    const interval = setInterval(advanceTip, TIP_CYCLE_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutRef.current);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [advanceTip]);

  const tip = tips[currentIndex];

  const sign = slideDirection === 'next' ? 1 : -1;
  let contentStyle: React.CSSProperties;
  if (slidePhase === 'out') {
    contentStyle = {
      transform: `translateX(${-sign * SLIDE_OFFSET_PX}px)`,
      opacity: 0,
      transition: `transform ${SLIDE_DURATION_MS}ms ease-in, opacity ${SLIDE_DURATION_MS}ms ease-in`,
    };
  } else if (slidePhase === 'entering') {
    contentStyle = {
      transform: `translateX(${sign * SLIDE_OFFSET_PX}px)`,
      opacity: 0,
    };
  } else if (slidePhase === 'in') {
    contentStyle = {
      transform: 'translateX(0)',
      opacity: 1,
      transition: `transform ${SLIDE_DURATION_MS}ms ease-out, opacity ${SLIDE_DURATION_MS}ms ease-out`,
    };
  } else {
    contentStyle = {transform: 'translateX(0)', opacity: 1};
  }

  return (
    <Transition mounted={mounted} transition="fade" duration={200}>
      {(transitionStyles) => (
        <Paper
          withBorder={true}
          radius="lg"
          p="md"
          mt="sm"
          style={transitionStyles}
          sx={(theme) => ({
            borderColor: theme.colors.blue[1],
            backgroundColor: theme.colors.blue[0],
            overflow: 'hidden',
          })}
          {...getTestProps({component: 'ChatInputTips', element: 'root'})}
        >
          <Flex align="flex-start" gap="sm">
            <Flex align="flex-start" gap="sm" style={{...contentStyle, flex: 1, minWidth: 0}}>
              <BenchlingIcon icon={tip.icon} className={classes.icon} />
              <div style={{flex: 1, minWidth: 0}}>
                <Text className={classes.title}>Tip: {tip.title}</Text>
                <Text className={classes.text}>{tip.text}</Text>
              </div>
            </Flex>
            <Flex align="center" gap={4} sx={{flexShrink: 0}}>
              <ActionIcon
                size="sm"
                variant="subtle"
                className={classes.navButton}
                onClick={handlePrevious}
                aria-label="Previous tip"
                {...getTestProps({component: 'ChatInputTips', element: 'previousButton'})}
              >
                <BenchlingIcon icon="chevron-left" />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="subtle"
                className={classes.navButton}
                onClick={handleNext}
                aria-label="Next tip"
                {...getTestProps({component: 'ChatInputTips', element: 'nextButton'})}
              >
                <BenchlingIcon icon="chevron-right" />
              </ActionIcon>
            </Flex>
          </Flex>
        </Paper>
      )}
    </Transition>
  );
}

export default function ChatInputTips(): JSX.Element | null {
  const {llmChatTips} = useRolloutFlags();
  const availableTips = CHAT_INPUT_TIPS.filter((tip) => isTipAvailable(tip));
  if (llmChatTips !== true) {
    return null;
  }
  if (availableTips.length === 0) {
    return null;
  }
  // Key the inner component by the set of available tip ids. If feature gating changes at runtime
  // (e.g. LD flag updates), this remounts the carousel with fresh state so `currentIndex` can't
  // point past the end of the new (shorter) tips list.
  const tipsKey = availableTips.map((tip) => tip.id).join(',');
  return <ChatInputTipsInner key={tipsKey} tips={availableTips} />;
}
